import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { usePreventRouteChange } from "@sol/routing";
import { Modal } from "@sol/uikit/feedback/Modal/Modal";

/** Protect both local editor cancellation and navigation while a form has unsaved changes. */
export const useUnsavedChanges = (isDirty: boolean) => {
    const [t] = useTranslation();
    const [modal, contextHolder] = Modal.useModal();
    const { resume, reset, force } = usePreventRouteChange(isDirty);

    const confirmationOptions = useMemo(
        () => ({
            title: t("admin.global.unsavedChanges.title"),
            content: t("admin.global.unsavedChanges.description"),
            okText: t("admin.global.unsavedChanges.discard"),
            cancelText: t("admin.global.unsavedChanges.keepEditing"),
            okButtonProps: { danger: true },
        }),
        [t],
    );

    const confirmDiscard = useCallback(
        (discard: () => void) => {
            if (!isDirty) {
                discard();
                return;
            }
            modal.confirm({
                ...confirmationOptions,
                onOk: () => {
                    force();
                    discard();
                },
            });
        },
        [isDirty, modal, confirmationOptions, force],
    );

    useEffect(() => {
        if (!resume) {
            return;
        }
        const confirmation = modal.confirm({
            ...confirmationOptions,
            onOk: resume,
            onCancel: reset,
        });
        return () => confirmation.destroy();
    }, [resume, reset, modal, confirmationOptions]);

    useEffect(() => {
        if (!isDirty) {
            return;
        }
        const preventUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
        };
        window.addEventListener("beforeunload", preventUnload);
        return () => window.removeEventListener("beforeunload", preventUnload);
    }, [isDirty]);

    return { confirmDiscard, contextHolder };
};
